import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { RDAvatar } from "./rd-avatar";
import { Badge } from "@/components/badge";
import { THEMED_ROW_HOVER, THEMED_RULE } from "@/lib/always-look";
import { cn } from "@/lib/utils";

// Types mirror the legacy server projection (apps/always-ai/src/lib/v2/rd-list.ts
// and coverage-data.ts) — the enriched GET /api/rds payload matches these shapes.
export type RdCoverageStatus = "deep" | "building" | "thin" | "no_recent";

export interface RdCoverageSummary {
  completedLast30Days: number;
  uploadsLast14Days: number;
  latestUploadAt: string | null;
  latestSessionDate: string | null;
  uploaderSummary: string;
  status: RdCoverageStatus;
  statusLabel: string;
}

export interface RdListItem {
  id: string;
  slug: string;
  name: string;
  /** When this RD was added to AlwaysAI; the RD header renders it as "Added". */
  createdAt: string;
  supervisorName: string | null;
  sessionCount: number;
  lastSession: {
    date: string;
    type: "initial" | "follow_up" | null;
  } | null;
  lastActivity: {
    description: string;
    timestamp: string;
  };
  coverage: RdCoverageSummary;
}

// Local port of legacy lib/v2/local-date.ts formatLocalDate — parses YYYY-MM-DD
// as local date parts (not new Date(iso)) to avoid timezone off-by-one.
type LocalDateFormat = "short" | "shortWithYear" | "longWithYear";

const LOCAL_DATE_FORMATS: Record<LocalDateFormat, Intl.DateTimeFormatOptions> = {
  short: { month: "short", day: "numeric" },
  shortWithYear: { month: "short", day: "numeric", year: "numeric" },
  longWithYear: { month: "long", day: "numeric", year: "numeric" },
};

export function formatLocalDate(
  isoDate: string | null,
  format: LocalDateFormat = "short"
): string {
  if (!isoDate) return "";

  const [year, month, day] = isoDate.split("-").map(Number);
  if (!year || !month || !day) return isoDate;

  return new Date(year, month - 1, day).toLocaleDateString(
    "en-US",
    LOCAL_DATE_FORMATS[format]
  );
}

function sessionTypeBadge(type: "initial" | "follow_up" | null) {
  if (!type) return null;

  return (
    <Badge variant="category">{type === "initial" ? "Initial" : "Follow-up"}</Badge>
  );
}

function coverageDotClass(status: RdCoverageStatus): string {
  switch (status) {
    case "deep":
      return "bg-success";
    case "building":
      return "bg-sky-500";
    case "thin":
      return "bg-sunlight-500";
    case "no_recent":
      return "bg-destructive";
  }
}

const MOBILE_LABEL =
  "md:hidden text-micro uppercase tracking-wider text-subtle font-medium";

function CoverageCell({ coverage }: { coverage: RdCoverageSummary }) {
  return (
    <div className="min-w-0">
      <p className={MOBILE_LABEL}>Coverage</p>
      <div className="flex items-center gap-2 min-w-0">
        <span
          className={cn(
            "h-2 w-2 rounded-full shrink-0",
            coverageDotClass(coverage.status)
          )}
        />
        <span className="text-sm font-medium text-foreground truncate">
          {coverage.statusLabel}
        </span>
      </div>
      <p className="mt-0.5 text-xs text-muted-foreground truncate">
        {coverage.completedLast30Days} complete in 30d · {coverage.uploaderSummary}
      </p>
    </div>
  );
}

function LastSessionCell({ lastSession }: { lastSession: RdListItem["lastSession"] }) {
  if (!lastSession) {
    return (
      <div>
        <p className={MOBILE_LABEL}>Last session</p>
        <span className="text-sm text-muted-foreground">&mdash;</span>
      </div>
    );
  }

  return (
    <div>
      <p className={MOBILE_LABEL}>Last session</p>
      <div className="flex items-center gap-2">
        <span className="text-sm text-foreground">
          {formatLocalDate(lastSession.date, "shortWithYear")}
        </span>
        {sessionTypeBadge(lastSession.type)}
      </div>
    </div>
  );
}

export function RDListRow({ rd }: { rd: RdListItem }) {
  return (
    <Link
      to="/rds/$rdId"
      params={{ rdId: rd.slug }}
      className={cn(
        "block bg-card hover:bg-muted border-b border-border transition-colors group",
        THEMED_RULE,
        THEMED_ROW_HOVER
      )}
    >
      <div className="grid gap-3 py-4 px-5 md:grid-cols-[minmax(0,2fr)_0.7fr_minmax(0,1.5fr)_minmax(0,1.2fr)_minmax(0,1.4fr)_24px] md:gap-4 md:items-center">
        <div className="flex items-center gap-3 min-w-0">
          <RDAvatar name={rd.name} />
          <div className="min-w-0">
            <p className="text-base font-semibold text-foreground truncate">
              {rd.name}
            </p>
            {rd.supervisorName && (
              <p className="text-sm text-subtle truncate">{rd.supervisorName}</p>
            )}
          </div>
        </div>

        <div>
          <p className="md:hidden text-micro uppercase tracking-wider text-subtle font-medium">
            Sessions
          </p>
          <p className="text-sm font-medium text-foreground">{rd.sessionCount}</p>
        </div>

        <CoverageCell coverage={rd.coverage} />

        <LastSessionCell lastSession={rd.lastSession} />

        <div className="min-w-0">
          <p className="md:hidden text-micro uppercase tracking-wider text-subtle font-medium">
            Last activity
          </p>
          <p className="text-sm text-muted-foreground truncate">
            {rd.lastActivity.description}
          </p>
        </div>

        <div className="hidden md:flex justify-end">
          <ChevronRight className="shrink-0 h-4 w-4 text-sand-300 group-hover:text-muted-foreground transition-colors" />
        </div>
        <div className="md:hidden flex items-center text-xs font-medium text-muted-foreground group-hover:text-foreground">
          Open RD
          <ChevronRight className="ml-1 h-3.5 w-3.5" />
        </div>
      </div>
    </Link>
  );
}
